import { useState, useEffect, useCallback } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/enhanced-button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useToast } from "@/hooks/use-toast";
import { UserPlus, MoreHorizontal, Loader2, ChevronDown, ChevronRight, Mail, RefreshCw } from "lucide-react";
import apiClient from "@/utils/httpClient";
import posthog from "@/lib/posthog";
import { UserOrganizationRole } from "@blinkbooks/types";
import { canManageTeam, ROLE_LABELS, ALL_ROLES } from "@/lib/permissions";
import { useOrganization } from "@/contexts/OrganizationContext";

interface TeamMember {
  id: string;
  userId: string;
  email: string;
  firstName: string;
  lastName: string;
  role: UserOrganizationRole;
}

interface PendingMember {
  id: string;
  email: string;
}

interface TeamTabProps {
  role?: UserOrganizationRole | null;
  currentUserId?: string;
}

function getInitials(firstName: string, lastName: string) {
  return `${firstName[0] ?? ''}${lastName[0] ?? ''}`.toUpperCase();
}

const ROLE_BADGE_VARIANT: Record<UserOrganizationRole, 'default' | 'secondary' | 'outline'> = {
  [UserOrganizationRole.FRACTIONAL_CFO]: 'default',
  [UserOrganizationRole.MANAGER]: 'secondary',
  [UserOrganizationRole.BOOKKEEPER]: 'secondary',
  [UserOrganizationRole.SHAREHOLDER]: 'outline',
};

function RoleBadge({ role }: { role: UserOrganizationRole }) {
  const variant = ROLE_BADGE_VARIANT[role];
  return (
    <Badge
      variant={variant}
      className={variant === 'default' ? "bg-foreground text-background hover:bg-foreground text-xs font-medium" : "text-xs font-medium"}
    >
      {ROLE_LABELS[role]}
    </Badge>
  );
}

const TeamTab = ({ role, currentUserId }: TeamTabProps) => {
  const { toast } = useToast();
  const isManager = canManageTeam(role);
  const { organizations, activeOrganizationId } = useOrganization();

  const [members, setMembers] = useState<TeamMember[]>([]);
  const [pending, setPending] = useState<PendingMember[]>([]);
  const [isLoadingTeam, setIsLoadingTeam] = useState(false);
  const [pendingExpanded, setPendingExpanded] = useState(true);

  // Invite modal
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<UserOrganizationRole>(UserOrganizationRole.MANAGER);
  const [selectedOrgIds, setSelectedOrgIds] = useState<Set<string>>(new Set());
  const [isInviting, setIsInviting] = useState(false);

  const openInviteDialog = () => {
    setSelectedOrgIds(activeOrganizationId ? new Set([activeOrganizationId]) : new Set());
    setInviteOpen(true);
  };

  const toggleOrgId = (id: string) => {
    setSelectedOrgIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const fetchTeam = useCallback(() => {
    setIsLoadingTeam(true);
    apiClient.get<{ members: TeamMember[]; pending: PendingMember[] }>('/organizations/team')
      .then((data) => {
        setMembers(data.members);
        setPending(data.pending);
      })
      .catch(() => {
        toast({ title: "Error", description: "Failed to load team members.", variant: "destructive" });
      })
      .finally(() => setIsLoadingTeam(false));
  }, [toast]);

  useEffect(() => {
    if (role) fetchTeam();
  }, [role, fetchTeam]);

  const handleInvite = async () => {
    const email = inviteEmail.trim().toLowerCase();
    if (!email || selectedOrgIds.size === 0) return;

    if (
      members.some((m) => m.email === email) ||
      pending.some((p) => p.email === email)
    ) {
      toast({ title: "Already on team", description: "This email is already a member or has a pending invite.", variant: "destructive" });
      return;
    }

    setIsInviting(true);
    try {
      await apiClient.post('/organizations/team/invite-multi', {
        email,
        role: inviteRole,
        organizationIds: Array.from(selectedOrgIds),
      });
      posthog.capture("team_member_invited", { role: inviteRole, organization_count: selectedOrgIds.size });
      toast({ title: "Invite sent", description: `${email} has been invited.` });
      setInviteOpen(false);
      setInviteEmail("");
      setInviteRole(UserOrganizationRole.MANAGER);
      setSelectedOrgIds(new Set());
      fetchTeam();
    } catch {
      toast({ title: "Error", description: "Failed to send invite.", variant: "destructive" });
    } finally {
      setIsInviting(false);
    }
  };

  const handleResendInvite = async (id: string) => {
    try {
      await apiClient.post(`/organizations/team/pending/${id}/resend`, {});
      posthog.capture("team_invite_resent", {});
      toast({ title: "Invite resent" });
    } catch {
      toast({ title: "Error", description: "Failed to resend invite.", variant: "destructive" });
    }
  };

  const handleCancelInvite = async (id: string) => {
    try {
      await apiClient.request<void>(`/organizations/team/pending/${id}`, { method: 'DELETE' });
      setPending((prev) => prev.filter((p) => p.id !== id));
      posthog.capture("team_invite_cancelled", {});
      toast({ title: "Invite cancelled" });
    } catch {
      toast({ title: "Error", description: "Failed to cancel invite.", variant: "destructive" });
    }
  };

  const handleRemoveMember = async (id: string) => {
    const removedRole = members.find((m) => m.id === id)?.role;
    try {
      await apiClient.request<void>(`/organizations/team/${id}`, { method: 'DELETE' });
      setMembers((prev) => prev.filter((m) => m.id !== id));
      posthog.capture("team_member_removed", { removed_role: removedRole });
      toast({ title: "Member removed" });
    } catch {
      toast({ title: "Error", description: "Failed to remove member.", variant: "destructive" });
    }
  };

  const handleChangeRole = async (id: string, newRole: UserOrganizationRole) => {
    const previousRole = members.find((m) => m.id === id)?.role;
    try {
      await apiClient.request<void>(`/organizations/team/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ role: newRole }),
      });
      setMembers((prev) => prev.map((m) => m.id === id ? { ...m, role: newRole } : m));
      posthog.capture("team_member_role_changed", { previous_role: previousRole, new_role: newRole });
      toast({ title: `Role updated to ${ROLE_LABELS[newRole]}` });
    } catch {
      toast({ title: "Error", description: "Failed to update role. The last Fractional CFO on a team can't be changed.", variant: "destructive" });
    }
  };

  const cfoCount = members.filter((m) => m.role === UserOrganizationRole.FRACTIONAL_CFO).length;
  const visibleMembers = isManager ? members : members.filter((m) => m.role === UserOrganizationRole.FRACTIONAL_CFO);

  return (
    <div className="p-6 max-w-4xl">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-foreground">Team Members</h1>
          <p className="text-sm text-muted-foreground mt-0.5">{isManager ? 'Manage access and roles for your organization' : 'View the Fractional CFOs in your organization'}</p>
        </div>
        {isManager && (
          <Button size="sm" onClick={openInviteDialog}>
            <UserPlus className="w-4 h-4 mr-2" />
            Invite Member
          </Button>
        )}
      </div>

      {isLoadingTeam ? (
        <div className="flex items-center gap-2 text-muted-foreground text-sm">
          <Loader2 className="w-4 h-4 animate-spin" />
          Loading team...
        </div>
      ) : (
        <>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="w-[260px]">Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead className="w-[180px]">Role</TableHead>
                  <TableHead className="w-[80px]">Status</TableHead>
                  {isManager && <TableHead className="w-[40px]" />}
                </TableRow>
              </TableHeader>
              <TableBody>
                {visibleMembers.map((member) => {
                  const isYou = member.userId === currentUserId;
                  const isLastCfo = cfoCount === 1 && member.role === UserOrganizationRole.FRACTIONAL_CFO;
                  const canEditThisRow = isManager && !isYou && !isLastCfo;
                  return (
                    <TableRow key={member.id}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-xs font-semibold text-muted-foreground shrink-0">
                            {getInitials(member.firstName, member.lastName)}
                          </div>
                          <span className="text-sm font-medium">
                            {member.firstName} {member.lastName}
                          </span>
                          {isYou && (
                            <Badge variant="outline" className="text-xs px-1.5 py-0 font-normal text-muted-foreground">
                              You
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">{member.email}</TableCell>
                      <TableCell>
                        {canEditThisRow ? (
                          <Select value={member.role} onValueChange={(v) => handleChangeRole(member.id, v as UserOrganizationRole)}>
                            <SelectTrigger className="h-7 text-xs w-full">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {ALL_ROLES.map((r) => (
                                <SelectItem key={r} value={r} className="text-xs">{ROLE_LABELS[r]}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <RoleBadge role={member.role} />
                        )}
                      </TableCell>
                      <TableCell>
                        <span className="text-xs text-emerald-600 font-medium">Active</span>
                      </TableCell>
                      {isManager && <TableCell>
                        {!isYou && !!currentUserId && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                className="text-destructive focus:text-destructive"
                                disabled={isLastCfo}
                                onClick={() => handleRemoveMember(member.id)}
                              >
                                Remove from team
                              </DropdownMenuItem>
                              {isLastCfo && (
                                <>
                                  <DropdownMenuSeparator />
                                  <p className="px-2 py-1.5 text-xs text-muted-foreground">The last Fractional CFO can't be removed.</p>
                                </>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </TableCell>}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          {isManager && pending.length > 0 && (
            <div className="mt-6">
              <button
                className="flex items-center gap-1.5 text-sm font-medium text-foreground mb-3 hover:text-muted-foreground transition-colors"
                onClick={() => setPendingExpanded((v) => !v)}
              >
                {pendingExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                Pending Invitations
                <Badge variant="secondary" className="ml-1 text-xs">{pending.length}</Badge>
              </button>
              {pendingExpanded && (
                <div className="rounded-md border divide-y">
                  {pending.map((p) => (
                    <div key={p.id} className="flex items-center justify-between px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center shrink-0">
                          <Mail className="w-3.5 h-3.5 text-muted-foreground" />
                        </div>
                        <span className="text-sm text-muted-foreground">{p.email}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="ghost" size="sm" className="h-7 text-xs gap-1.5" onClick={() => handleResendInvite(p.id)}>
                          <RefreshCw className="w-3 h-3" />
                          Resend
                        </Button>
                        <Button variant="ghost" size="sm" className="h-7 text-xs text-muted-foreground" onClick={() => handleCancelInvite(p.id)}>
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}

      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Invite Member</DialogTitle>
            <DialogDescription>
              Send an invitation to join one or more of your companies.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Email address</label>
              <Input
                type="email"
                placeholder="teammate@company.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleInvite(); }}
                disabled={isInviting}
                autoFocus
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Role</label>
              <Select value={inviteRole} onValueChange={(v) => setInviteRole(v as UserOrganizationRole)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ALL_ROLES.map((r) => (
                    <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-medium mb-1 block">Companies</Label>
              <p className="text-xs text-muted-foreground mb-2">
                Select every company this person should have access to — they'll be invited to all of them in one email.
              </p>
              {organizations.length === 0 ? (
                <p className="text-sm text-muted-foreground">No companies found.</p>
              ) : (
                <ScrollArea className="max-h-40 border rounded-md p-2">
                  <div className="space-y-2">
                    {organizations.map((org) => (
                      <div key={org.id} className="flex items-center gap-2">
                        <Checkbox
                          id={`invite-org-${org.id}`}
                          checked={selectedOrgIds.has(org.id)}
                          onCheckedChange={() => toggleOrgId(org.id)}
                          disabled={isInviting}
                        />
                        <label
                          htmlFor={`invite-org-${org.id}`}
                          className="text-sm cursor-pointer select-none"
                        >
                          {org.name}
                        </label>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" size="sm" onClick={() => setInviteOpen(false)} disabled={isInviting}>
                Cancel
              </Button>
              <Button size="sm" onClick={handleInvite} disabled={!inviteEmail.trim() || selectedOrgIds.size === 0 || isInviting}>
                {isInviting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Send Invite
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TeamTab;
